import { useState } from "react";
import axios from "axios";
import { Loader2, Search, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getApiUrl } from "@/utils/api";
import { getModuleTypeLabel } from "@/components/cms/moduleDefaults";
import InstancePreviewModal from "@/components/admin/InstancePreviewModal";

const API = getApiUrl();

/**
 * FindSection
 * -----------
 * Admin → CMS → Find tab. Free-text requirement input that is sent to the
 * AI-powered /api/admin/find-instances endpoint, then renders ranked matches
 * with explanation + match score. Clicking a match opens the standard
 * InstancePreviewModal so the admin can inspect the instance + jump to the
 * live URL where it appears.
 */
export default function FindSection() {
  const [requirements, setRequirements] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null); // null = no search yet
  const [totalSearched, setTotalSearched] = useState(0);
  const [previewInstance, setPreviewInstance] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const text = (requirements || "").trim();
    if (!text) {
      toast.error("Please describe what you're looking for first.");
      return;
    }
    setLoading(true);
    setMatches(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/find-instances`,
        { requirements: text },
        { withCredentials: true, timeout: 120000 }
      );
      setMatches(data.matches || []);
      setTotalSearched(data.total_searched || 0);
      if ((data.matches || []).length === 0) {
        toast.info("No matching module instances found — try refining your requirement.");
      }
    } catch (err) {
      console.error("find-instances failed:", err);
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 80) return "bg-green-100 text-green-800 border-green-300";
    if (score >= 60) return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-slate-100 text-slate-700 border-slate-300";
  };

  return (
    <div className="space-y-6" data-testid="cms-find-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Find Module Instances
          </CardTitle>
          <CardDescription>
            Describe what you need and we&apos;ll match it against every Module Instance using AI.
            Click a result to preview it in detail.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="find-requirements" className="text-sm font-medium">
                Requirements
              </label>
              <Textarea
                id="find-requirements"
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                placeholder='e.g. "a welcoming hero banner that promotes selling a home", "a footer with our company tagline", "a property gallery for our flagship listing"…'
                rows={4}
                className="resize-y"
                data-testid="find-requirements-textarea"
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="submit"
                disabled={loading || !requirements.trim()}
                data-testid="find-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching…
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Find
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {matches !== null && (
        <div className="space-y-3" data-testid="find-results-list">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {matches.length === 0
                ? "No matches"
                : `${matches.length} match${matches.length === 1 ? "" : "es"}`}
            </h3>
            <span className="text-xs text-muted-foreground">
              Searched {totalSearched} module instance{totalSearched === 1 ? "" : "s"}
            </span>
          </div>

          {matches.length === 0 && (
            <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">
              No module instances satisfied that requirement. Try a different phrasing or be more specific.
            </div>
          )}

          {matches.map((m) => (
            <button
              key={m.instance_id}
              type="button"
              onClick={() => setPreviewInstance(m)}
              className="w-full text-left border rounded-md hover:border-primary/40 hover:shadow-sm transition-all bg-card"
              data-testid={`find-match-${m.instance_id}`}
            >
              <div className="p-4 flex items-start gap-4">
                {/* Score bar (left) */}
                <div className="w-20 flex-shrink-0 text-center">
                  <div
                    className={`inline-flex items-center justify-center px-2 py-1 rounded-md border text-sm font-semibold ${scoreColor(m.score)}`}
                  >
                    {m.score}%
                  </div>
                  <Progress
                    value={m.score}
                    className="h-1 mt-2"
                  />
                </div>

                {/* Content (right) */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{m.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {getModuleTypeLabel(m.module_type)}
                    </Badge>
                    {!m.enabled && (
                      <Badge variant="secondary" className="text-xs">disabled</Badge>
                    )}
                  </div>
                  {m.explanation && (
                    <p className="text-sm text-muted-foreground mt-1">{m.explanation}</p>
                  )}
                </div>

                <Eye className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      )}

      <InstancePreviewModal
        instance={previewInstance}
        open={!!previewInstance}
        onOpenChange={(o) => { if (!o) setPreviewInstance(null); }}
      />
    </div>
  );
}
